import { useState, useEffect, useMemo } from 'react';
import PaginationControls from '../common/PaginationControls';
import NoticeToast from '../common/NoticeToast';
import {
  getAllUsers,
  createUser,
  updateUser,
  deleteUser,
  getAllRoles,
  createRole,
  updateRole,
  deleteRole
} from '../../api/admin';
import { useNotice } from '../../hooks/useNotice';
import './DirecteurRoleManagement.css';

const USERS_ITEMS_PER_PAGE = 30;
const ROLES_ITEMS_PER_PAGE = 5;

const isProtectedAdminRole = (role) => {
  const code = (role?.code || role?.role_code || '').toLowerCase();
  const name = (role?.nom || role?.role_nom || '').toLowerCase();
  return code.includes('admin')
    || code.includes('specialiste')
    || name.includes('admin')
    || name.includes('spécialiste')
    || name.includes('specialiste');
};

const isProtectedAdminUser = (user) => isProtectedAdminRole(user);

export default function DirecteurRoleManagement() {
  const { notice, showNotice, clearNotice } = useNotice();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    nom: '',
    email: '',
    mot_de_passe: '',
    id_role: ''
  });

  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({
    code: '',
    nom: ''
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [usersPage, setUsersPage] = useState(1);
  const [rolesPage, setRolesPage] = useState(1);
  const [activeTab, setActiveTab] = useState('users');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [usersData, rolesData] = await Promise.all([
        getAllUsers(),
        getAllRoles()
      ]);
      setUsers(usersData);
      setRoles(rolesData);
    } catch (err) {
      setError('Erreur lors du chargement des données.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenUserModal = (userItem = null) => {
    if (userItem && isProtectedAdminUser(userItem)) {
      showNotice('La direction adjointe ne peut pas modifier les informations du compte administrateur.', 'warning');
      return;
    }

    if (userItem) {
      setEditingUser(userItem);
      setUserForm({
        nom: userItem.nom,
        email: userItem.email,
        mot_de_passe: '',
        id_role: userItem.id_role
      });
    } else {
      setEditingUser(null);
      setUserForm({ nom: '', email: '', mot_de_passe: '', id_role: '' });
    }
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setUserModalOpen(false);
    setEditingUser(null);
    setUserForm({ nom: '', email: '', mot_de_passe: '', id_role: '' });
  };

  const handleUserFormChange = (field, value) => {
    setUserForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (!userForm.nom || !userForm.email || !userForm.id_role) {
      showNotice('Veuillez remplir tous les champs obligatoires.', 'warning');
      return;
    }

    const selectedRole = roles.find((role) => role.id === parseInt(userForm.id_role, 10));
    if (selectedRole && isProtectedAdminRole(selectedRole)) {
      showNotice('La direction adjointe ne peut pas attribuer le role administrateur.', 'warning');
      return;
    }

    try {
      if (editingUser) {
        await updateUser(editingUser.id, userForm);
        showNotice('Utilisateur modifie avec succes.', 'success');
      } else {
        if (!userForm.mot_de_passe) {
          showNotice('Le mot de passe est obligatoire pour un nouvel utilisateur.', 'warning');
          return;
        }
        await createUser(userForm);
        showNotice('Utilisateur cree avec succes.', 'success');
      }
      handleCloseUserModal();
      loadData();
    } catch (err) {
      showNotice('Erreur lors de la sauvegarde de l\'utilisateur.', 'error');
      console.error(err);
    }
  };

  const handleDeleteUser = async (userId) => {
    const userToDelete = users.find((user) => user.id === userId);
    if (userToDelete && isProtectedAdminUser(userToDelete)) {
      showNotice('La direction adjointe ne peut pas supprimer un compte administrateur.', 'warning');
      return;
    }

    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur?')) return;

    try {
      await deleteUser(userId);
      showNotice('Utilisateur supprime avec succes.', 'success');
      loadData();
    } catch (err) {
      showNotice('Erreur lors de la suppression.', 'error');
      console.error(err);
    }
  };

  const handleOpenRoleModal = (roleItem = null) => {
    if (roleItem && isProtectedAdminRole(roleItem)) {
      showNotice('La direction adjointe ne peut pas modifier le role administrateur.', 'warning');
      return;
    }

    if (roleItem) {
      setEditingRole(roleItem);
      setRoleForm({ code: roleItem.code, nom: roleItem.nom });
    } else {
      setEditingRole(null);
      setRoleForm({ code: '', nom: '' });
    }
    setRoleModalOpen(true);
  };

  const handleCloseRoleModal = () => {
    setRoleModalOpen(false);
    setEditingRole(null);
    setRoleForm({ code: '', nom: '' });
  };

  const handleRoleFormChange = (field, value) => {
    setRoleForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveRole = async (e) => {
    e.preventDefault();
    if (!roleForm.code || !roleForm.nom) {
      showNotice('Veuillez remplir tous les champs obligatoires.', 'warning');
      return;
    }

    if (isProtectedAdminRole(roleForm)) {
      showNotice('La direction adjointe ne peut pas creer ou modifier un role administrateur.', 'warning');
      return;
    }

    try {
      if (editingRole) {
        await updateRole(editingRole.id, roleForm);
        showNotice('Role modifie avec succes.', 'success');
      } else {
        await createRole(roleForm);
        showNotice('Role cree avec succes.', 'success');
      }
      handleCloseRoleModal();
      loadData();
    } catch (err) {
      showNotice('Erreur lors de la sauvegarde du role.', 'error');
      console.error(err);
    }
  };

  const handleDeleteRole = async (roleId) => {
    const roleToDelete = roles.find((role) => role.id === roleId);
    if (roleToDelete && isProtectedAdminRole(roleToDelete)) {
      showNotice('La direction adjointe ne peut pas supprimer le role administrateur.', 'warning');
      return;
    }

    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce rôle ? Les utilisateurs associés doivent être réaffectés d\'abord.')) return;

    try {
      await deleteRole(roleId);
      showNotice('Role supprime avec succes.', 'success');
      loadData();
    } catch (err) {
      showNotice('Erreur lors de la suppression du role.', 'error');
      console.error(err);
    }
  };

  const filteredUsers = users.filter((user) => {
    if (filterRole && user.id_role !== parseInt(filterRole, 10)) return false;
    if (
      searchTerm
      && !user.nom.toLowerCase().includes(searchTerm.toLowerCase())
      && !user.email.toLowerCase().includes(searchTerm.toLowerCase())
    ) {
      return false;
    }
    return true;
  });

  const sortedFilteredUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) => {
      const nameCompare = (a.nom || '').localeCompare(b.nom || '');
      if (nameCompare !== 0) {
        return nameCompare;
      }
      return a.id - b.id;
    });
  }, [filteredUsers]);

  const sortedRoles = useMemo(() => {
    return [...roles].sort((a, b) => {
      const nameCompare = (a.nom || '').localeCompare(b.nom || '');
      if (nameCompare !== 0) {
        return nameCompare;
      }
      return a.id - b.id;
    });
  }, [roles]);

  const usersTotalPages = useMemo(() => Math.max(1, Math.ceil(sortedFilteredUsers.length / USERS_ITEMS_PER_PAGE)), [sortedFilteredUsers.length]);
  const rolesTotalPages = useMemo(() => Math.max(1, Math.ceil(sortedRoles.length / ROLES_ITEMS_PER_PAGE)), [sortedRoles.length]);

  const paginatedUsers = useMemo(() => {
    const start = (usersPage - 1) * USERS_ITEMS_PER_PAGE;
    return sortedFilteredUsers.slice(start, start + USERS_ITEMS_PER_PAGE);
  }, [sortedFilteredUsers, usersPage]);

  const paginatedRoles = useMemo(() => {
    const start = (rolesPage - 1) * ROLES_ITEMS_PER_PAGE;
    return sortedRoles.slice(start, start + ROLES_ITEMS_PER_PAGE);
  }, [sortedRoles, rolesPage]);

  const activeUsersCount = useMemo(() => users.length, [users.length]);
  const totalRolesCount = useMemo(() => roles.length, [roles.length]);
  const filteredUsersCount = useMemo(() => filteredUsers.length, [filteredUsers.length]);
  const assignableRoles = useMemo(() => roles.filter((role) => !isProtectedAdminRole(role)), [roles]);

  useEffect(() => {
    setUsersPage(1);
  }, [searchTerm, filterRole]);

  useEffect(() => {
    if (usersPage > usersTotalPages) {
      setUsersPage(usersTotalPages);
    }
  }, [usersPage, usersTotalPages]);

  useEffect(() => {
    if (rolesPage > rolesTotalPages) {
      setRolesPage(rolesTotalPages);
    }
  }, [rolesPage, rolesTotalPages]);

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Chargement...</div>;
  }

  return (
    <div className="role-management-container directeur-role-management">
      <div className="role-management-header">
        <h1>Gestion des utilisateurs et des rôles</h1>
        <p className="subtitle">Administration des accès et des permissions de la direction adjointe</p>
      </div>

      <div className="stats-strip">
        <div className="stat-pill">
          <span className="stat-label">Utilisateurs</span>
          <span className="stat-value">{activeUsersCount}</span>
        </div>
        <div className="stat-pill">
          <span className="stat-label">Rôles</span>
          <span className="stat-value">{totalRolesCount}</span>
        </div>
        <div className="stat-pill">
          <span className="stat-label">Résultat filtre</span>
          <span className="stat-value">{filteredUsersCount}</span>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="management-tabs" role="tablist" aria-label="Sections de gestion">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'users'}
          className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Gestion des utilisateurs
          <span className="tab-count">{filteredUsersCount}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'roles'}
          className={`tab-button ${activeTab === 'roles' ? 'active' : ''}`}
          onClick={() => setActiveTab('roles')}
        >
          Gestion des rôles
          <span className="tab-count">{totalRolesCount}</span>
        </button>
      </div>

      {activeTab === 'users' && (
      <div className="management-section section-users" role="tabpanel" aria-label="Gestion des utilisateurs">
        <div className="section-header">
          <h2>Utilisateurs</h2>
          <button className="btn-primary" onClick={() => handleOpenUserModal()}>
            + Ajouter un utilisateur
          </button>
        </div>

        <div className="filters">
          <input
            type="text"
            placeholder="Rechercher par nom ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="filter-input"
          />
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="filter-select"
          >
            <option value="">Tous les rôles</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.nom}
              </option>
            ))}
          </select>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="no-data">Aucun utilisateur trouvé.</div>
        ) : (
          <div className="table-responsive">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Email</th>
                  <th>Rôle</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((user) => (
                  <tr key={user.id}>
                    <td><strong>{user.nom}</strong></td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`role-badge ${isProtectedAdminUser(user) ? 'role-badge-protected' : ''}`}>
                        {roles.find((role) => role.id === user.id_role)?.nom || '—'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn-edit"
                        onClick={() => handleOpenUserModal(user)}
                        title="Modifier l'utilisateur"
                        disabled={isProtectedAdminUser(user)}
                      >
                        ✏️ Modifier
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteUser(user.id)}
                        title="Supprimer l'utilisateur"
                        disabled={isProtectedAdminUser(user)}
                      >
                        🗑️ Supprimer
                      </button>
                      {isProtectedAdminUser(user) && <span className="protected-note">Compte protégé</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <PaginationControls currentPage={usersPage} totalPages={usersTotalPages} onPageChange={setUsersPage} />
          </div>
        )}
      </div>
      )}

      {activeTab === 'roles' && (
      <div className="management-section section-roles" role="tabpanel" aria-label="Gestion des rôles">
        <div className="section-header">
          <h2>Rôles</h2>
          <button className="btn-primary" onClick={() => handleOpenRoleModal()}>
            + Ajouter un rôle
          </button>
        </div>

        {roles.length === 0 ? (
          <div className="no-data">Aucun rôle trouvé.</div>
        ) : (
          <div className="table-responsive">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Nom</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRoles.map((role) => (
                  <tr key={role.id}>
                    <td><strong>{role.code}</strong></td>
                    <td>
                      {role.nom}
                      {isProtectedAdminRole(role) && <span className="protected-note">Rôle protégé</span>}
                    </td>
                    <td>
                      <button
                        className="btn-edit"
                        onClick={() => handleOpenRoleModal(role)}
                        title="Modifier le rôle"
                        disabled={isProtectedAdminRole(role)}
                      >
                        ✏️ Modifier
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteRole(role.id)}
                        title="Supprimer le rôle"
                        disabled={isProtectedAdminRole(role)}
                      >
                        🗑️ Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <PaginationControls currentPage={rolesPage} totalPages={rolesTotalPages} onPageChange={setRolesPage} />
          </div>
        )}
      </div>
      )}

      {userModalOpen && (
        <div className="modal-overlay" onClick={handleCloseUserModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingUser ? 'Modifier' : 'Créer'} un utilisateur</h3>
              <button className="modal-close" onClick={handleCloseUserModal}>✕</button>
            </div>
            <form onSubmit={handleSaveUser} className="modal-form">
              <div className="form-group">
                <label>Nom *</label>
                <input
                  type="text"
                  value={userForm.nom}
                  onChange={(e) => handleUserFormChange('nom', e.target.value)}
                  placeholder="Nom complet"
                  required
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => handleUserFormChange('email', e.target.value)}
                  placeholder="email@example.com"
                  required
                />
              </div>
              {!editingUser && (
                <div className="form-group">
                  <label>Mot de passe *</label>
                  <input
                    type="password"
                    value={userForm.mot_de_passe}
                    onChange={(e) => handleUserFormChange('mot_de_passe', e.target.value)}
                    placeholder="Mot de passe"
                    required={!editingUser}
                  />
                </div>
              )}
              {editingUser && (
                <div className="form-group">
                  <label>Nouveau mot de passe (optionnel)</label>
                  <input
                    type="password"
                    value={userForm.mot_de_passe}
                    onChange={(e) => handleUserFormChange('mot_de_passe', e.target.value)}
                    placeholder="Laisser vide pour conserver le mot de passe"
                  />
                </div>
              )}
              <div className="form-group">
                <label>Rôle *</label>
                <select
                  value={userForm.id_role}
                  onChange={(e) => handleUserFormChange('id_role', e.target.value)}
                  required
                >
                  <option value="">-- Sélectionner un rôle --</option>
                  {assignableRoles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.nom}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={handleCloseUserModal}>
                  Annuler
                </button>
                <button type="submit" className="btn-save">
                  {editingUser ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {roleModalOpen && (
        <div className="modal-overlay" onClick={handleCloseRoleModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingRole ? 'Modifier' : 'Créer'} un rôle</h3>
              <button className="modal-close" onClick={handleCloseRoleModal}>✕</button>
            </div>
            <form onSubmit={handleSaveRole} className="modal-form">
              <div className="form-group">
                <label>Code *</label>
                <input
                  type="text"
                  value={roleForm.code}
                  onChange={(e) => handleRoleFormChange('code', e.target.value)}
                  placeholder="Ex: directeur_adjoint"
                  required
                />
              </div>
              <div className="form-group">
                <label>Nom *</label>
                <input
                  type="text"
                  value={roleForm.nom}
                  onChange={(e) => handleRoleFormChange('nom', e.target.value)}
                  placeholder="Nom du rôle"
                  required
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={handleCloseRoleModal}>
                  Annuler
                </button>
                <button type="submit" className="btn-save">
                  {editingRole ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <NoticeToast notice={notice} onClose={clearNotice} />
    </div>
  );
}
